import React, { useEffect, useRef, useState } from "react";

export default function DropDownMenu({
  isAuthenticated, // Estado de autenticación
  avatar, // URL del avatar
  name, // Nombre del usuario
  menuItems = [], // Lista de ítems del menú
  onLogin, // Función para iniciar sesión
  onNavigate, // Función para manejar la navegación (navegador)
  role, // Rol del usuario
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const trigger = useRef(null);
  const dropdown = useRef(null);

  // Cerrar el dropdown al hacer clic fuera de él
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  }, [dropdownOpen]);

  // Cerrar el dropdown al presionar la tecla Esc
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, [dropdownOpen]);

  // Componente que renderiza cada ítem del menú, basado en el rol
  const renderMenuItem = (item, index, lastGroup) => {
    // Si el ítem tiene una propiedad `roles` y el rol actual del usuario no está en ese array, no se muestra el ítem
    if (item.roles && !item.roles.includes(role)) {
      return null;
    }

    return (
      <div key={index} className="flex flex-col">
        {/* Verificamos si el ítem tiene un título de grupo, y si es diferente al último grupo renderizado */}
        {item.group && item.group !== lastGroup && (
          <div className="mb-2 mt-6 px-4 text-sm font-semibold uppercase tracking-wider text-sky-900">
            {item.group}
          </div>
        )}
        <button
          className={`flex items-center px-4 py-2 text-left text-sky-800 hover:bg-sky-600 hover:text-white ${
            item.active ? "bg-sky-600 text-white" : ""
          }`}
          onClick={() => {
            if (item.link) onNavigate(item.link);
            if (item.action) item.action();
          }}
        >
          {item.icon && <span className="mr-3">{item.icon}</span>}{" "}
          {/* Icono opcional */}
          <span className="text-sm">{item.label}</span>
        </button>
      </div>
    );
  };

  return (
    <section className="bg-sky-400 px-4">
      <div className="container">
        <div className="flex flex-col">
          <div className="relative inline-block">
            {isAuthenticated ? (
              <>
                <button
                  ref={trigger}
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center p-4 text-left"
                >
                  <div className="relative mr-4 h-[42px] w-[42px] rounded-full">
                    <img
                      src={avatar}
                      alt="avatar"
                      className="h-full w-full rounded-full object-cover object-center"
                    />
                    <span className="absolute -right-0.5 -top-0.5 block h-[14px] w-[14px] rounded-full border-[2.3px] border-white bg-[#219653]"></span>
                  </div>
                  <span className="text-base font-medium text-sky-200 dark:text-white">
                    {name}
                  </span>
                  <span className="pl-[10px] text-sky-200 duration-100 dark:text-white">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={`fill-current ${dropdownOpen ? "-scale-y-100" : ""}`}
                    >
                      <path d="M10 14.25C9.8125 14.25 9.65625 14.1875 9.5 14.0625L2.3125 7C2.03125 6.71875 2.03125 6.28125 2.3125 6C2.59375 5.71875 3.03125 5.71875 3.3125 6L10 12.5312L16.6875 5.9375C16.9688 5.65625 17.4062 5.65625 17.6875 5.9375C17.9688 6.21875 17.9688 6.65625 17.6875 6.9375L10.5 14C10.3437 14.1562 10.1875 14.25 10 14.25Z" />
                    </svg>
                  </span>
                </button>

                <div
                  ref={dropdown}
                  onFocus={() => setDropdownOpen(true)}
                  onBlur={() => setDropdownOpen(false)}
                  className={`shadow-card dark:shadow-box-dark absolute right-0 top-full z-40 w-[200px] space-y-1 rounded bg-sky-400 p-2 dark:bg-sky-400 ${
                    dropdownOpen ? "block" : "hidden"
                  }`}
                >
                  {menuItems.map((item, index) => {
                    // Seguimiento del último grupo para verificar el agrupador
                    const lastGroup =
                      index === 0 ? null : menuItems[index - 1].group;
                    return renderMenuItem(item, index, lastGroup);
                  })}
                </div>
              </>
            ) : (
              // Si no está autenticado, mostrar botón de iniciar sesión
              <button
                onClick={onLogin}
                className="w-40 rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-600"
              >
                Iniciar sesión
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
